function usableAudioTrack(track) {
  return track?.kind === 'audio' && track?.readyState !== 'ended'
}

// Keeps the existing LiveKit publication and swaps only its underlying MediaStreamTrack.
// The original LiveKit-owned microphone track is never stopped here and is retained for rollback.
export class EnhancedVoicePublication {
  constructor({ getPublication, source = 'microphone', logger = console } = {}) {
    this.getPublication = getPublication
    this.source = source
    this.logger = logger
    this.localTrack = null
    this.rawTrack = null
    this.enhancedTrack = null
    this.processor = null
    this.mode = 'raw'
    this.queue = Promise.resolve()
  }

  sync(processor) {
    return this._enqueue(() => this._sync(processor || null))
  }

  restoreRaw() {
    return this._enqueue(() => this._restoreRaw())
  }

  reset() {
    return this._enqueue(async () => {
      this._releaseProcessor()
      this.localTrack = null
      this.rawTrack = null
      this.enhancedTrack = null
      this.mode = 'raw'
      return this.mode
    })
  }

  _enqueue(operation) {
    const next = this.queue.then(operation, operation)
    this.queue = next.catch(() => {})
    return next
  }

  _currentPublication() {
    return this.getPublication?.(this.source) || null
  }

  _adoptPublication(publication) {
    const localTrack = publication?.track || null
    if (!localTrack) return null
    const currentMediaTrack = localTrack.mediaStreamTrack || null
    const publicationChanged = localTrack !== this.localTrack
    const externallyReplaced = this.mode === 'enhanced' && currentMediaTrack !== this.enhancedTrack
    if (publicationChanged || externallyReplaced) {
      this._releaseProcessor()
      this.localTrack = localTrack
      this.rawTrack = currentMediaTrack
      this.enhancedTrack = null
      this.mode = 'raw'
    } else if (!this.rawTrack) {
      this.rawTrack = currentMediaTrack
    }
    return localTrack
  }

  async _sync(processor) {
    const publication = this._currentPublication()
    const localTrack = this._adoptPublication(publication)
    if (!localTrack || !usableAudioTrack(this.rawTrack)) {
      this._releaseProcessor()
      this.mode = 'raw'
      return this.mode
    }
    if (processor?.isPublishingEnabled?.() !== true) return this._restoreRaw()

    let nextTrack
    try {
      nextTrack = await processor.getTrack()
    } catch (error) {
      this.logger?.error?.('[livekit] enhanced microphone acquisition failed; keeping raw microphone', error)
      if (processor !== this.processor) processor?.releaseTrack?.()
      return this._restoreRaw()
    }
    if (!usableAudioTrack(nextTrack)) {
      if (processor !== this.processor) processor?.releaseTrack?.()
      return this._restoreRaw()
    }
    if (this.mode === 'enhanced' && nextTrack === this.enhancedTrack && processor === this.processor) return this.mode

    nextTrack.enabled = this.rawTrack.enabled
    try {
      await localTrack.replaceTrack(nextTrack, { userProvidedTrack: true, stopProcessor: false })
    } catch (error) {
      this.logger?.error?.('[livekit] enhanced microphone swap failed; rolling back to raw microphone', error)
      try {
        await localTrack.replaceTrack(this.rawTrack, { userProvidedTrack: true, stopProcessor: false })
      } catch (rollbackError) {
        this.logger?.error?.('[livekit] raw microphone rollback failed', rollbackError)
      }
      if (processor !== this.processor) processor?.releaseTrack?.()
      this._releaseProcessor()
      this.enhancedTrack = null
      this.mode = 'raw'
      return this.mode
    }

    if (this.processor && this.processor !== processor) this.processor.releaseTrack?.()
    this.processor = processor
    this.enhancedTrack = nextTrack
    this.mode = 'enhanced'
    return this.mode
  }

  async _restoreRaw() {
    if (this.mode === 'enhanced' && this.localTrack && usableAudioTrack(this.rawTrack)) {
      try {
        this.rawTrack.enabled = this.enhancedTrack?.enabled ?? this.rawTrack.enabled
        await this.localTrack.replaceTrack(this.rawTrack, { userProvidedTrack: true, stopProcessor: false })
      } catch (error) {
        this.logger?.error?.('[livekit] raw microphone restore failed', error)
        return this.mode
      }
    }
    this._releaseProcessor()
    this.enhancedTrack = null
    this.mode = 'raw'
    return this.mode
  }

  _releaseProcessor() {
    this.processor?.releaseTrack?.()
    this.processor = null
  }
}
